import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_PRODUCT_KEYS } from "@shared/constants/queryKeys";
import type { Product } from "@shared/types/product";
import type { PaginatedResponse, PaginationMeta } from "@shared/types/index";

interface UseProductsParams {
  page?: number;
  limit?: number;
  search?: string;
}

type ApiProductsResponse = PaginatedResponse<Product> & {
  meta?: PaginationMeta;
  pagination?: PaginationMeta;
};

export function useProducts(params: UseProductsParams = {}) {
  const { page = 1, limit = 10, search } = params;

  return useQuery({
    queryKey: ADMIN_PRODUCT_KEYS.list({ page, limit, search: search ?? "" }),
    queryFn: async (): Promise<PaginatedResponse<Product>> => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) {
        searchParams.append("search", search);
      }

      const response = await apiClient.get<ApiProductsResponse>(
        `/product?${searchParams}`
      );

      if (!response || typeof response !== "object") {
        return {
          success: false,
          data: [],
          pagination: {
            total: 0,
            page,
            limit,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        };
      }

      const { success = true, message, data = [] } = response;

      const meta: PaginationMeta | undefined =
        ("meta" in response && response.meta ? response.meta : undefined) ??
        ("pagination" in response && response.pagination
          ? response.pagination
          : undefined);

      const normalizedMeta: PaginationMeta = {
        total: meta?.total ?? data.length,
        page: meta?.page ?? page,
        limit: meta?.limit ?? limit,
        totalPages:
          meta?.totalPages ??
          Math.max(
            1,
            Math.ceil((meta?.total ?? data.length) / (meta?.limit ?? limit))
          ),
        hasNext: meta?.hasNext ?? false,
        hasPrev: meta?.hasPrev ?? false,
      };

      return {
        success,
        message,
        data,
        pagination: normalizedMeta,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
